import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Badge } from '@optools/components/ui/badge';
import { GET_PLAYBACK_OVERRIDES } from '@optools/services/operator-gateway/overrides/playback/queries';
import { PlaybackOverride } from '@optools/services/operator-gateway/overrides/playback/types';
import { CreatePlaybackOverride } from '@optools/tools/overrides/components/create-playback-override';
import { DeletePlaybackOverride } from '@optools/tools/overrides/components/delete-playback-override';
import { UpdatePlaybackOverride } from '@optools/tools/overrides/components/update-playback-override';
import { transformPlaybackOverride } from '@optools/utils/overrides/playback';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

export const Route = createFileRoute('/_auth/overrides/playback')({
  component: RouteComponent,
});

export function RouteComponent() {
  const { data, loading } = useQuery(GET_PLAYBACK_OVERRIDES);

  const overrides = data?.playbackOverrides?.map(transformPlaybackOverride) ?? [];

  const columns = useMemo<ColumnDef<PlaybackOverride>[]>(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
        id: 'ID',
      },
      {
        accessorKey: 'userId',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Okta ID" />,
        id: 'Okta ID',
      },
      {
        accessorKey: 'clientType',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Client Type" />,
        id: 'Client Type',
      },
      {
        accessorKey: 'experience',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Experience" />,
        id: 'Experience',
      },
      {
        accessorFn: (row) => row.features.join(' '),
        accessorKey: 'features',
        cell: ({ cell }) => {
          return (
            <div className="flex flex-wrap gap-2 py-1">
              {cell.row.original.features.map((feature) => (
                <Badge key={feature} variant="secondary">
                  {feature}
                </Badge>
              ))}
            </div>
          );
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Features" />,
        id: 'Features',
        size: 300,
      },
      {
        cell: ({ cell }) => {
          return (
            <div className="flex items-center justify-end gap-2">
              <UpdatePlaybackOverride override={cell.row.original} />
              <DeletePlaybackOverride overrideId={cell.row.original.id} />
            </div>
          );
        },
        header: () => <span className="flex justify-end">Actions</span>,
        id: 'actions',
      },
    ],
    [],
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <DataTable columns={columns} data={overrides}>
      <CreatePlaybackOverride />
    </DataTable>
  );
}
